import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft, 
  Calendar, 
  Eye, 
  Tag, 
  Clock,
  Share2,
  BookOpen
} from 'lucide-react';
import NavigationHeader from '../components/NavigationHeader';
import blogService from '../services/blogService';
import { sanitizedHtml } from '../utils/security';
import toast from 'react-hot-toast';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadBlogPost = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await blogService.getBlogBySlug(slug);
      if (response.success) {
        setBlog(response.data);
        
        // Load related blogs
        const relatedResponse = await blogService.getRelatedBlogs(slug, 3);
        if (relatedResponse.success) {
          setRelatedBlogs(relatedResponse.data);
        }
      } else {
        setError(response.error);
      }
    } catch (error) {
      setError(t('blog.failedToLoadPost'));
    } finally {
      setLoading(false);
    }
  }, [slug, t]);

  useEffect(() => {
    if (slug) {
      loadBlogPost();
    }
  }, [slug, loadBlogPost]);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({
          title: blog.title,
          text: blog.excerpt,
          url: url
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success(t('blog.linkCopied'));
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        toast.error(t('blog.failedToShare'));
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryLabel = (category) => {
    return t(`blog.categories.${category.toLowerCase()}`, category.replace('_', ' '));
  };

  const estimateReadingTime = (content) => {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return minutes;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a15507] mx-auto mb-4"></div>
          <p className="text-gray-300">{t('blog.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] flex items-center justify-center">
        <div className="text-center text-white">
          <div className="text-6xl mb-4">☕</div>
          <h1 className="text-3xl font-bold mb-4">{t('blog.postNotFound')}</h1>
          <p className="text-gray-300 mb-6">{t('blog.postNotFoundDescription')}</p>
          <button
            onClick={() => navigate('/blog')}
            className="btn-primary"
          >
            {t('blog.backToBlog')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827]">
      <NavigationHeader />
      
      {/* Header with top padding for fixed nav */}
      <div className="bg-gradient-to-r from-[#a15507] to-[#924e06] text-white py-20 pt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/blog')}
            className="flex items-center text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t('blog.backToBlog')}
          </button>
          
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="px-3 py-1 bg-white/20 text-white text-sm rounded-full">
                {getCategoryLabel(blog.category)}
              </span>
              {blog.featured && (
                <span className="px-3 py-1 bg-green-600 text-white text-sm rounded-full">
                  {t('blog.featured')}
                </span>
              )}
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{blog.title}</h1>
            
            <p className="text-xl text-white/90 mb-6 max-w-3xl mx-auto">
              {blog.excerpt}
            </p>
            
            <div className="flex items-center justify-center gap-6 text-white/80 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(blog.publishedAt || blog.createdAt)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{estimateReadingTime(blog.content)} {t('blog.minRead')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                <span>{blog.viewCount} {t('blog.views')}</span>
              </div>
            </div>
            
            <button
              onClick={handleShare}
              className="flex items-center mx-auto mt-6 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            >
              <Share2 className="w-5 h-5 mr-2" />
              {t('blog.share')}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            <article className="card">
              {blog.featuredImageUrl && (
                <div className="aspect-video bg-gray-700 rounded-t-lg overflow-hidden mb-6">
                  <img 
                    src={blog.featuredImageUrl} 
                    alt={blog.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              <div className="p-6">
                <div className="prose prose-invert max-w-none">
                  <div dangerouslySetInnerHTML={sanitizedHtml(blog.content)} />
                </div>
                
                {/* Tags */}
                {blog.tags && blog.tags.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-gray-700">
                    <div className="flex items-center gap-2 mb-3">
                      <Tag className="w-4 h-4 text-[#a15507]" />
                      <span className="text-sm font-medium text-white">{t('blog.tags')}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {blog.tags.map((tag, index) => (
                        <span 
                          key={index}
                          className="px-3 py-1 bg-gray-700 text-gray-300 text-sm rounded-full hover:bg-gray-600 transition-colors"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </article>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              {/* Related Posts */}
              {relatedBlogs.length > 0 && (
                <div className="card">
                  <h3 className="text-lg font-semibold text-white mb-4">{t('blog.relatedPosts')}</h3>
                  <div className="space-y-4">
                    {relatedBlogs.map((relatedBlog) => (
                      <div 
                        key={relatedBlog.id}
                        className="cursor-pointer group"
                        onClick={() => navigate(`/blog/${relatedBlog.slug}`)}
                      >
                        {relatedBlog.featuredImageUrl && (
                          <div className="aspect-video bg-gray-700 rounded-lg overflow-hidden mb-2">
                            <img 
                              src={relatedBlog.featuredImageUrl} 
                              alt={relatedBlog.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        )}
                        <h4 className="text-sm font-medium text-white group-hover:text-[#a15507] transition-colors line-clamp-2">
                          {relatedBlog.title}
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(relatedBlog.publishedAt || relatedBlog.createdAt)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Blog Stats */}
              <div className="card">
                <h3 className="text-lg font-semibold text-white mb-4">{t('blog.postStats')}</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">{t('blog.category')}</span>
                    <span className="text-white">{getCategoryLabel(blog.category)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">{t('blog.published')}</span>
                    <span className="text-white">{formatDate(blog.publishedAt || blog.createdAt)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">{t('blog.readingTime')}</span>
                    <span className="text-white">{estimateReadingTime(blog.content)} {t('blog.minRead')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">{t('blog.views')}</span>
                    <span className="text-white">{blog.viewCount}</span>
                  </div>
                </div>
              </div>

              {/* Call to Action */}
              <div className="card bg-gradient-to-r from-[#a15507] to-[#924e06] text-white">
                <div className="text-center">
                  <BookOpen className="w-8 h-8 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold mb-2">{t('blog.ctaTitle')}</h3>
                  <p className="text-white/90 text-sm mb-4">{t('blog.ctaDescription')}</p>
                  <button
                    onClick={() => navigate('/blog')}
                    className="w-full bg-white text-[#a15507] font-medium py-2 px-4 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {t('blog.exploreMore')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
